
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'

import NewButton from '@/ui-kit/NewButton/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @module/components
import CouriersTableRow from '@/modules/NewDispatcher/components/CouriersTableRow/index.vue'
import CouriersTableRowActions from '@/modules/NewDispatcher/components/CouriersTableRowActions/index.vue'

// @types
import {
    Courier,
    CouriersExportExcelParamsT,
    GetCouriersParams,
} from '@/new-services/types/couriers'
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { CompanyT } from '@/core/types/companies.types'

// @services
import couriersAPI from '@/new-services/couriers.service'
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { useMaska } from '@/common/composable/useMaska'
import { useFile } from '@/common/composable/useFile'

// @realisations
import { companies } from '@/core/realisations/companies.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'

// @constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import { CourierTypeT } from '@/core/types/couriers.types'

const COLUMNS = [
    {
        name: 'ID',
        key: 'id',
    },
    {
        name: 'ФИО / ИИН',
        key: 'fullName',
    },
    {
        name: 'Код 1C',
        key: 'code1C',
    },
    {
        name: 'Телефон',
        key: 'phoneNumber',
    },
    {
        name: 'Поставщик',
        key: 'company',
        customHint: 'name',
    },
    {
        name: 'Сектор диспетчера',
        key: 'dispatcherSector',
    },
    {
        name: 'Гос.номер / марка авто',
        key: 'car',
    },
    {
        name: 'Дата создания',
        key: 'createdAt',
    },
    {
        name: 'Смена',
        key: 'schedule',
    },
    {
        name: 'Статус',
        key: 'status',
    },
]

const COURIER_STATUSES = [
    {
        id: 1,
        name: 'На проверке(СБ)',
    },
    {
        id: 2,
        name: 'Активный',
    },
    {
        id: 3,
        name: 'В архиве',
    },
    {
        id: 4,
        name: 'Отказ СБ',
    },
]

const COURIER_SHIFTS = [
    {
        id: 1,
        name: 'На смене',
    },
    {
        id: 2,
        name: 'Не на смене',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        FailedFetch,
        NewTable,
        NewLimit,
        NewPagination,
        NewSelect,
        NewInput,
        CouriersTableRow,
        CouriersTableRowActions,
        NewModal,
        Loader,
    },
    setup() {
        const notifications = useNotifications()
        const { havePermissions } = useAccount()
        const { unmaskNumbers } = useMaska()
        const { saveFile, saveFileByURL } = useFile()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Courier[],
            current_page: 1,
            total_pages: 1,
            total_items: 20,
            limit: 50,
        })

        const filters_state = reactive({
            is_opened: false,
            //
            dispatcherSectorIds: [] as number[],
            createdAtFrom: null as string | null,
            createdAtUntil: null as string | null,
            statusIds: [] as number[],
            companyId: null as number | null,
            name: null as string | null,
            iin: null as string | null,
            phoneNumber: null as string | null,
            carModel: null as string | null,
            carNumber: null as string | null,
            id: null as number | null,
            shiftId: null as number | null,
            typesIds: [] as number[],
        })

        const excel_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            fromDate: null as string | null,
            toDate: null as string | null,
        })

        const phone_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            id: null as number | null,
            phoneNumber: null as string | null,
        })

        const qr_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            img: null as string | null,
        })

        const dispatcher_sectors_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DispatcherSector[],
        })

        const companies_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CompanyT[],
        })

        const types_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CourierTypeT[],
        })

        const loadCompanies = async () => {
            try {
                companies_state.is_loading = true

                const response = await companies.getAllList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                    statusId: 2,
                })
                companies_state.list = response.data

                companies_state.is_failed = false
            } catch (error) {
                companies_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка компаний произошла ошибка',
                })
            } finally {
                companies_state.is_loading = false
            }
        }

        const loadTypes = async () => {
            try {
                types_state.is_loading = true

                const response = await couriers.getCourierTypes()
                types_state.list = response.data

                types_state.is_failed = false
            } catch (error) {
                types_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка типов курьера произошла ошибка',
                })
            } finally {
                types_state.is_loading = false
            }
        }

        const loadCouriers = async () => {
            try {
                state.is_loading = true

                const phoneNumber = filters_state.phoneNumber
                    ? unmaskNumbers(filters_state.phoneNumber)
                    : null

                const params: GetCouriersParams = {
                    page: state.current_page,
                    limit: state.limit,
                    dispatcherSectorIds: filters_state.dispatcherSectorIds,
                    createdAtFrom: filters_state.createdAtFrom,
                    createdAtUntil: filters_state.createdAtUntil,
                    statusIds: filters_state.statusIds,
                    companyId: filters_state.companyId,
                    name: filters_state.name,
                    iin: filters_state.iin,
                    phoneNumber,
                    carModel: filters_state.carModel,
                    carNumber: filters_state.carNumber,
                    id: filters_state.id,
                    shiftId: filters_state.shiftId,
                    typesIds: filters_state.typesIds,
                }

                const response = await couriersAPI.getCouriers(params)
                state.list = response.data.data
                state.total_pages = response.data.meta.last_page
                state.total_items = response.data.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadDispatcherSectors = async () => {
            try {
                dispatcher_sectors_state.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                dispatcher_sectors_state.list = response.data.data

                dispatcher_sectors_state.is_failed = false
            } catch (error) {
                console.error(error)
                dispatcher_sectors_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов диспетчера произошла ошибка',
                })
            } finally {
                dispatcher_sectors_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCouriers()
            loadDispatcherSectors()
            loadCompanies()
            loadTypes()
        })

        const onChangePage = (page: number) => {
            state.current_page = page
            loadCouriers()
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadCouriers()
        }

        const resetFilters = () => {
            filters_state.createdAtFrom = null
            filters_state.createdAtUntil = null
            filters_state.dispatcherSectorIds = []
            filters_state.statusIds = []
            filters_state.companyId = null
            filters_state.name = null
            filters_state.iin = null
            filters_state.phoneNumber = null
            filters_state.carModel = null
            filters_state.carNumber = null
            filters_state.id = null
            filters_state.shiftId = null
            filters_state.typesIds = []

            loadCouriers()
        }

        const toggleFilters = () => {
            filters_state.is_opened = !filters_state.is_opened
        }

        const getExcelParams = (): CouriersExportExcelParamsT => {
            return {
                dispatcherSectorIds: filters_state.dispatcherSectorIds,
                statusIds: filters_state.statusIds,
                companyId: filters_state.companyId,
                name: filters_state.name,
                iin: filters_state.iin,
                phoneNumber: filters_state.phoneNumber,
                carModel: filters_state.carModel,
                carNumber: filters_state.carNumber,
                id: filters_state.id,
                shiftId: filters_state.shiftId,
                //
                fromDate: excel_state.fromDate,
                toDate: excel_state.toDate,
            }
        }

        const toggleExcel = () => (excel_state.is_opened = !excel_state.is_opened)
        const toggleSetPhone = (id?: number) => {
            if (id) phone_state.id = id
            phone_state.is_opened = !phone_state.is_opened
        }

        const loadQr = async (id: number) => {
            try {
                qr_state.is_loading = true

                const response = await couriersAPI.getQr(id)
                const encodedSvg =
                    'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(response.data)
                qr_state.img = encodedSvg
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при загрузке QR`,
                })
            } finally {
                qr_state.is_loading = false
            }
        }

        const toggleQr = (id?: number) => {
            if (id) loadQr(id)
            qr_state.is_opened = !qr_state.is_opened
        }

        const exportExcel = async () => {
            try {
                excel_state.is_loading = true

                const params = getExcelParams()
                const response = await couriersAPI.exportExcel(params)

                saveFile(response.data, 'Курьеры')
                toggleExcel()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                excel_state.is_loading = false
            }
        }

        const setPhone = async () => {
            if (!phone_state.phoneNumber || !phone_state.id) return

            try {
                phone_state.is_loading = true
                await couriersAPI.setPhone(phone_state.id, unmaskNumbers(phone_state.phoneNumber))
                toggleSetPhone()
                loadCouriers()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                phone_state.is_loading = false
            }
        }

        const downloadQr = () => {
            if (!qr_state.img) return
            saveFileByURL(qr_state.img)
        }

        const isCanUpdate = computed(() => {
            if (havePermissions(['dispatcher.courier-update', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const submitFilters = () => {
            state.current_page = 1
            loadCouriers()
        }

        return {
            PHONE_NUMBER_MASK,
            COLUMNS,
            COURIER_STATUSES,
            COURIER_SHIFTS,
            //
            state,
            filters_state,
            excel_state,
            phone_state,
            types_state,
            qr_state,
            dispatcher_sectors_state,
            companies_state,
            loadCouriers,
            loadDispatcherSectors,
            onChangePage,
            onLimitChange,
            resetFilters,
            toggleFilters,
            toggleExcel,
            loadCompanies,
            loadTypes,
            exportExcel,
            toggleSetPhone,
            setPhone,
            toggleQr,
            //
            isCanUpdate,
            downloadQr,
            submitFilters,
        }
    },
})
